import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import { useWebSocket } from '../contexts/WebSocketContext';
import * as apiService from '../services/apiService';
import { DispenseTransaction } from '../types/DrinkTypes';
import { useAppSettings } from '../contexts/AppSettingsContext';
import VideoPlayer from '../components/VideoPlayer';

interface LocationState {
    transaction_id: string;
}

const DispensingScreen: React.FC = () => {
    const { settings, appSettingsReady } = useAppSettings();
    const navigate = useNavigate();
    const { onMessage, offMessage } = useWebSocket();
    const [dispensingComplete, setDispensingComplete] = useState(false);
    const [dispensingData, setDispensingData] = useState<{ status: string; message: string } | null>(null);

    const MySwal = withReactContent(Swal);

    const location = useLocation();
    const state = location.state as LocationState;
    const transactionId = state?.transaction_id;

    const isPaymentEnabled = settings.paymentEnabled;

    const dispensingDelay = settings.dispensingDelay * 1000;
    const disableWebSocket = settings.disableWebSocket;

    const handleAnimationComplete = () => {
        if (!dispensingData) return;

        if (dispensingData.status === 'Success') {
            navigate('/');
        } else {
            MySwal.fire({
                title: 'Dispensing Failed',
                html: `${dispensingData.message}. Redirecting to home...`,
                icon: 'error',
                timer: 5000,
                showConfirmButton: false,
                willClose: () => {
                    navigate('/');
                }
            });
        }
    };

    useEffect(() => {
        if (!transactionId) {
            console.error("No transaction ID provided. Redirecting to select-drink.");
            navigate('/select-drink');
            return;
        }
        const handleDispensingCompleted = (data: any) => {
            console.log("Dispensing status:", data.status, "Message:", data.message);
            setDispensingData({ status: data.status, message: data.message });
            setDispensingComplete(true);
        };

        if (disableWebSocket) {
            const timeoutId = setTimeout(() => {
                handleDispensingCompleted({ status: 'Success', message: 'Dispensing completed successfully.' });
            }, 5000);
            return () => clearTimeout(timeoutId);
        } else {
            const timeoutId = setTimeout(async () => {
                try {
                    const currentTransaction: DispenseTransaction = {
                        transaction_id: transactionId || ''
                    };

                    await apiService.cancelCurrentDispensing(currentTransaction);

                    MySwal.fire({
                        title: 'Dispensing Cancelled',
                        html: `The dispensing has been cancelled due to timeout. Redirecting to home...`,
                        icon: 'info',
                        timer: 5000,
                        showConfirmButton: false,
                        willClose: () => {
                            navigate('/');
                        }
                    });
                } catch (error) {
                    console.error("Error cancelling dispensing:", error);
                }
            }, dispensingDelay);

            onMessage('dispensingCompletedEvent', handleDispensingCompleted);

            return () => {
                clearTimeout(timeoutId);
                offMessage('dispensingCompletedEvent', handleDispensingCompleted);
            };
        }
    }, [onMessage, offMessage, navigate, MySwal, transactionId, disableWebSocket, dispensingDelay, settings, appSettingsReady]);

    if (!appSettingsReady) {
        return (
            <div className="w-[1080px] h-[1920px] flex justify-center items-center bg-gray-100">
                <div className="text-2xl text-gray-500">Loading...</div>
            </div>
        );
    }


    return (
        <div data-layer="DISPENSING PAGE" className="DispensingPage w-[1080px] h-[1920px] relative bg-white overflow-hidden">
            <img data-layer="background" className="w-[1280px] h-[1920px] left-0 top-0 absolute" src="/assets/images/new_design/home/fond/image-fond-kissd-3.png" />
            <div data-layer="Rectangle 2" className="Rectangle2 w-[1080px] h-[1920px] left-0 top-0 absolute bg-teal-500/40" />
            <div data-layer="Rectangle 6" className="Rectangle6 w-[906px] h-[1775px] left-[87px] top-[239px] absolute bg-white/30 rounded-[62px] shadow-[6px_7px_27.700000762939453px_-11px_rgba(0,0,0,0.42)]" />

            {isPaymentEnabled ? (
                <div data-layer="main-text" className="w-[746px] h-[465px] left-[167px] top-[480px] absolute text-center text-[90px] font-extrabold font-['Montserrat'] leading-[115px]">
                    <div className="w-full text-center text-white text-5xl uppercase mb-8">Please wait</div>

                    <span className="text-white uppercase">Your kissd <br /></span>
                    <span className="text-white uppercase">water is<br /></span>
                    <span className="text-white uppercase"> filling up...</span>
                </div>
            ) : (
                <div data-layer="main-text" className="w-[746px] h-[465px] left-[167px] top-[480px] absolute text-center text-[80px] font-extrabold font-['Montserrat'] leading-[94px]">
                    <div className="w-full text-center text-white text-5xl uppercase mb-8">Please wait</div>
                    <span className="text-white">Payment will be made at<br /></span>
                    <span className="text-white"> the cash desk after receipt of your drink.</span>
                </div>
            )}

            <div className="w-[500px] h-[500px] left-[290px] top-[1050px] absolute rounded-full">
                <VideoPlayer
                    src="/assets/images/new_design/dispensing/Animation-Bottle.mp4"
                    type="video/mp4"
                    className="w-full h-full object-cover rounded-full"
                    shouldRestart={true}
                    restartAfterDuration={2000}
                    totalVideoDuration={3000}
                    isComplete={dispensingComplete}
                    onAnimationComplete={handleAnimationComplete}
                />
            </div>

            <img data-layer="freepik__3d-floating-transparent-bubbles-with-green-and-blu__24199 1" className="Freepik3dFloatingTransparentBubblesWithGreenAndBlu241991 w-72 h-96 left-[-50px] top-[-65px] absolute mix-blend-screen" src="/assets/images/new_design/payment/bubbles-1.svg" />
            <img data-layer="freepik__3d-floating-transparent-bubbles-with-green-and-blu__24199 2" className="Freepik3dFloatingTransparentBubblesWithGreenAndBlu241992 w-[450px] h-[975px] left-[782px] top-[1200px] absolute origin-top-left rotate-[14.04deg] mix-blend-screen" src="/assets/images/new_design/payment/bubbles-2.svg" />
            <img data-layer="bulles-2-fond-ecran 1" className="Bulles2FondEcran1 w-48 h-[588px] left-[824px] top-[-50px] absolute origin-top-left rotate-[-16.71deg] mix-blend-soft-light" src="/assets/images/new_design/payment/bulles-ecran-1.svg" />
            <img data-layer="bulles-2-fond-ecran 2" className="Bulles2FondEcran2 w-48 h-[588px] left-[15px] top-[863px] absolute origin-top-left rotate-[8.99deg] mix-blend-soft-light" src="/assets/images/new_design/payment/bulles-ecran-2.svg" />
            </div>
    );

    
};

export default DispensingScreen;
